import { useRef, useState } from "react";
import { Loader2, FileSpreadsheet, Sparkles, Upload, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";

export function CsvImportDialog({ open, onOpenChange, restaurantId, onImported }) {
  const csvFileRef = useRef(null);
  const [csvAnalyzing, setCsvAnalyzing] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvMapping, setCsvMapping] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvAnalyzing(true); setCsvPreview(null); setCsvMapping(null);
    try {
      const result = await api.importInventoryCSV(restaurantId, file, true);
      setCsvPreview(result); setCsvMapping(result.suggested_mapping || null);
      toast.success("CSV analizado correctamente");
    } catch (err) { toast.error(err.message || "Error al analizar CSV"); }
    finally { setCsvAnalyzing(false); if (csvFileRef.current) csvFileRef.current.value = ""; }
  };

  const handleCsvImport = async () => {
    if (!csvPreview) return;
    setCsvImporting(true);
    try {
      const file = csvFileRef.current?.files?.[0];
      const result = await api.importInventoryCSV(restaurantId, csvPreview._file || file, false, csvMapping);
      toast.success(`Importados ${result.imported_count || 0} ingredientes`);
      onOpenChange(false); setCsvPreview(null); setCsvMapping(null);
      onImported();
    } catch (err) { toast.error(err.message || "Error al importar"); }
    finally { setCsvImporting(false); }
  };

  const handleClose = (o) => { if (!o) { onOpenChange(false); setCsvPreview(null); setCsvMapping(null); } };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-amber-700" /> Importar inventario desde CSV
          </DialogTitle>
          <DialogDescription>Sube un archivo CSV con tus ingredientes. Columnas esperadas: nombre, stock, unidad, coste/ud, stock minimo</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input ref={csvFileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvFile} className="hidden" />
          {!csvPreview ? (
            <div className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/60 hover:bg-primary/5 transition-all"
              onClick={() => !csvAnalyzing && csvFileRef.current?.click()} data-testid="csv-drop-area">
              {csvAnalyzing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm font-medium">Analizando archivo...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Haz clic para seleccionar CSV</p>
                  <p className="text-xs text-muted-foreground">Formatos: .csv, .xlsx, .xls</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium">Vista previa</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {csvPreview.total_rows || csvPreview.preview_rows?.length || 0} filas detectadas
                  {csvPreview.detected_columns && ` — Columnas: ${csvPreview.detected_columns.join(", ")}`}
                </p>
              </div>
              {csvPreview.suggested_mapping && (
                <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Mapeo sugerido por IA
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {Object.entries(csvPreview.suggested_mapping).map(([key, val]) => (
                      <Badge key={key} variant="outline" className="text-[10px]">{key} → {val}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setCsvPreview(null); setCsvMapping(null); }}>Cancelar</Button>
                <Button onClick={handleCsvImport} disabled={csvImporting} data-testid="confirm-csv-import">
                  {csvImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Importar
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
